import React, { useState, useEffect } from "react";
import Dropdown from "../../../components/Dropdown";
import CustomPagination from "../../../components/CustomPagination";
import { useNavigate } from "react-router-dom";
import { 
  getJobList, 
  setCurrentPage, 
  getOptionsMaster,
  setFilterOption,
  deleteJobByIds,
  updateJobStatus,
  handleSortByHeader,
  TableHeader,
  setItemNameDelete,
  setResultImport,
} from "../../../reducers/companyJob";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useSelector, useDispatch } from "react-redux";
import { formatDateTime, getPeriodicTaskStatus } from "../../../utils/format";
import ConfirmPopup from "../../../components/ConfirmPopup"
import { PERMISSIONS, RECRUITMENT_STATUS, RECRUITMENT_STATUS_OPTION_TYPE, STAFF_TYPE, PERIODIC_TASK_STATUS } from '../../../constants/index';
import CustomButton from "../../../components/CustomButton";
import { InfoIcon } from "../../../components/Icons";

const jobStatusOptionsIcon = [
  {
    id: 1,
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
      >
        <circle cx="6" cy="6" r="6" fill="#FFC700" />
      </svg>
    ),
    name: "非公開",
    value: "private",
  },
  {
    id: 2,
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
      >
        <circle cx="6" cy="6" r="6" fill="#06C270" />
      </svg>
    ),
    name: "募集中",
    value: "recruiting",
  },
  {
    id: 3,
    name: "募集終了",
    value: "closed",
  },
  {
    id: 4,
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
      >
        <circle cx="6" cy="6" r="6" fill="#BBBBBB" />
      </svg>
    ),
    name: "公開予約",
    value: "scheduled",
    disabled: true,
    hidden: true,
  },
];

const JobList = ({ jobList }: any) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState<JSX.Element | string>("");
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const currentUser = useSelector((state: any) => state.currentUser);
  const { staffPermission, staffInfo } = currentUser;
  const {
    loading,
    success,
    error,
    currentPage,
    countItems,
    pageSize,
    optionsMaster,
    sortBy,
    sortOrder,
    searchQuery,
    filterOption,
    tableHeaders,
    itemNameDelete, actionName, actionStatus
  } = useSelector((state: any) => state.companyJob);

  useEffect(() => {
    const data = {
      option: RECRUITMENT_STATUS_OPTION_TYPE,
    }
    dispatch(getOptionsMaster(data));
    dispatch(setResultImport({}))
  }, []);

  useEffect(() => {
    if (actionStatus == 'fulfilled' && actionName == 'deleteJobByIds' && success) {
      let page = currentPage
      if(page > 1 && jobList.length == selectedRows.length) {
        page -= 1
        dispatch(setCurrentPage(page));
      }
      const data = {
        search: searchQuery,
        page: page,
        pageSize: pageSize,
        sortBy: sortBy,
        sortOrder: sortOrder,
        option: filterOption?.id,
      }
      dispatch(getJobList(data));

      setSelectedRows([]);
    }
  }, [actionStatus, actionName, success]);

  const dynamicStatusOptions = optionsMaster
    .filter((option: any) => option.name === RECRUITMENT_STATUS_OPTION_TYPE)
    .flatMap((option: any) =>
      option.option_list.map((subOption: any) => ({
        id: subOption.id,
        name: subOption.label,
        value: subOption.value,
      }))
    );

  const jobStatusOptions = jobStatusOptionsIcon.map(status => {
    const matched = dynamicStatusOptions.find(
      opt => opt.value === status.value
    );

    if (matched) {
      return {
        ...status,
        id: matched.id,
        name: matched.name,
      };
    }

    return status;
  });
  const filterJobStatusOptions = [...jobStatusOptions];
  filterJobStatusOptions.unshift({
    name: 'ステータス',
    id: 0,
    icon: '',
  });
    

  const handleUpdateJobStatus = (value: any, jobId: number) => {
    const dataUpdateStatus = {
      id: jobId,
      status: value.id,
      requestBody: jobList,
    }
    dispatch(updateJobStatus(dataUpdateStatus));
  }

  const handleSelectAll = () => {
    const currentPageIds = new Set(jobList.map((job: any) => job.id));
    const isAllCurrentPageSelected = jobList.length > 0 && jobList.every(
      (job: any) => selectedRows.some((selectedRow: any) => selectedRow.id === job.id)
    );

    if (isAllCurrentPageSelected) {
      setSelectedRows(selectedRows.filter((job: any) => !currentPageIds.has(job.id)));
      return;
    }

    const selectedPageRows = new Map(selectedRows.map((job: any) => [job.id, job]));
    jobList.forEach((job: any) => selectedPageRows.set(job.id, job));
    setSelectedRows(Array.from(selectedPageRows.values()));
  };

  const isAllCurrentPageSelected = jobList.length > 0 && jobList.every(
    (job: any) => selectedRows.some((selectedRow: any) => selectedRow.id === job.id)
  );

  const handleSelectRow = (item: any) => {
    const selectedIndex = selectedRows.map(n => n.id).indexOf(item?.id);
    let newSelected: any = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRows, item);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRows.slice(1));
    } else if (selectedIndex === selectedRows.length - 1) {
      newSelected = newSelected.concat(selectedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedRows.slice(0, selectedIndex),
        selectedRows.slice(selectedIndex + 1)
      );
    }
    setSelectedRows(newSelected);
  };

  const handleChangePage = (pageNumber: any, pageSizeInput: any) => {
    dispatch(setCurrentPage(pageNumber));
  };

  const handleSort = (sortHeader: TableHeader) => {
    dispatch(handleSortByHeader(sortHeader));
  };

  const handleFilterOption = (value: number) => {
    dispatch(setFilterOption(value));
    dispatch(setCurrentPage(1));

    const data = {
      search: searchQuery,
      page: 1,
      pageSize: pageSize,
      sortBy: sortBy,
      sortOrder: sortOrder,
      option: value?.id,
    }
    dispatch(getJobList(data));
  };

  const handleConfirm = (e: any) => {
    e.preventDefault();
    setOpen(false);
    if(selectedRows.length > 0){
      const dataDelete = {
        ids: selectedRows.map(item => item.id).join(','),
      }
      dispatch(deleteJobByIds(dataDelete));
    }
    
  };

  const handleDeleleJob = (e: any) => {
    e.preventDefault();
    if(selectedRows.length > 0){
      setOpen(true);
      dispatch(setItemNameDelete(selectedRows.length));
      setConfirmText(
        <>
          選択した{selectedRows.length}件の求人を削除します。<br />
          よろしいですか？
        </>
      );
    }
  }

  const handleClickOnTitle = (job: any) => {
    if(job.status?.value != RECRUITMENT_STATUS.PRIVATE) {
      navigate("/company/job/detail/" + job.id);
      return;
    }
    else {
      return;
    }
  }

  const handleClickOnDetailBtn = (job: any) => {
    if(job.status?.value != RECRUITMENT_STATUS.PRIVATE) {
      navigate("/company/job/detail/" + job.id);
      return;
    }
    else {
      return;
    }
  }

  const getSelectedStatusOption = (item) => {
    if (!item.is_public && item.is_public_scheduled && [RECRUITMENT_STATUS.RECRUITING, RECRUITMENT_STATUS.CLOSED].includes(item.status?.value)) {
      return jobStatusOptions.find(option => option.value === 'scheduled');
    }

    return jobStatusOptions.find(option => option.id === item.status.id);
  };

  const getAccessStatusOption = (item) => {
    if (item.status?.value == RECRUITMENT_STATUS.PRIVATE || !staffPermission[PERMISSIONS.JOB_EDIT]) {
      return true
    }
    if(!item.is_public && item.is_public_scheduled && [RECRUITMENT_STATUS.RECRUITING, RECRUITMENT_STATUS.CLOSED].includes(item.status?.value)) {
      return true
    }

    return false
  };
  
  return (
    <>
      <div className="rounded-t-lg px-4 py-2 bg-white border-b alu-border">
        <div className="flex items-center">
          <div className="flex items-center gap-4 flex-1">
            {staffPermission[PERMISSIONS.JOB_NEW] && (
            <CustomButton
              onClick={() => navigate("/company/job/register")}
              size="sm"
            >
              求人を作成
            </CustomButton>
            )}
            <div className="shrink-0 min-w-[150px]">
              <Dropdown
                inputWidth="full"
                size="filter"
                customStyle="h-[1.75rem]"
                options={filterJobStatusOptions}
                value={filterOption}
                onChange={(value: any) => handleFilterOption(value)}
                placeholder="ステータス"
              />
            </div>
            {staffPermission[PERMISSIONS.JOB_DELETE] && (
            <CustomButton
              variant="text"
              onClick={((e: any) => {handleDeleleJob(e)})}
              disabled={selectedRows.length > 0 ? false : true} 
            >
              削除
            </CustomButton>
            )}
          </div>
          {staffPermission[PERMISSIONS.JOB_NEW] && (
          <CustomButton
            onClick={() => navigate("/company/job/import")}
            variant="outline"
            size="sm"
            customStyle="shrink-0"
          >
            CSVで一括アップロード
          </CustomButton>
          )}
        </div>
      </div>
      <div className="bg-white rounded-b-lg">
        <table className="table-auto w-full">
          <thead className="border-b alu-border px-4 py-2">
            <tr>
              {staffPermission[PERMISSIONS.JOB_DELETE] && (
              <th className="px-4 py-2 text-left w-[3.5rem]">
                <button onClick={handleSelectAll} disabled={jobList.length === 0} className="flex items-center">
                  {!isAllCurrentPageSelected ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_8574_230)">
                        <path
                          d="M19 5V19H5V5H19ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z"
                          fill={jobList.length === 0 ? "#BBBBBB" : "#808080"}
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_8574_230">
                          <rect width="24" height="24" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_8574_236)">
                        <path
                          d="M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.11 21 21 20.1 21 19V5C21 3.9 20.11 3 19 3ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"
                          fill="#FF861A"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_8574_236">
                          <rect width="24" height="24" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  )}
                </button>
              </th>
              )} 
              {tableHeaders.map((header:TableHeader, idx:number) => (
                !(header.checkPermission && !staffPermission[PERMISSIONS.JOB_EDIT]) && (
                <th key={idx} className={`${staffPermission[PERMISSIONS.JOB_DELETE] ? "px-2" : "px-4"} py-2 text-left ${header.customStyle1}`}>
                  <div 
                    onClick={() => header.isSort && handleSort(header)} 
                    className={`flex items-center ${header.customStyle2} ${header.isSort ? 'hover:cursor-pointer' : ''}`}
                  >
                    <p className={`text-sm leading-[1.3125] ${header.field === sortBy ? "font-bold alu-text-base-2 underline underline-offset-4" : "font-normal alu-text-base-1"}`}>
                      {header.title}
                    </p>
                    {header.isSort && (
                      <button className={`${sortOrder === "asc" && sortBy === header.field ? 'rotate-180' : 'rotate-0'}`}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <g clipPath="url(#clip0_8574_176)">
                            <path d="M7 10L12 15L17 10H7Z" fill={`${header.field === sortBy ? "#343A3D" : "#808080"}`} />
                          </g>
                          <defs>
                            <clipPath id="clip0_8574_176">
                              <rect width="24" height="24" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                      </button>
                    )}
                  </div>
                </th>
                )
              ))}
            </tr>
          </thead>
          <tbody>
            {jobList.map((job: any, idx: any) => (
              <tr key={idx} className="border-t border-t-[#E0E0E0] hover:bg-[#fff3e5]">
                {staffPermission[PERMISSIONS.JOB_DELETE] && (
                <td className="px-4 py-4 text-left font-normal text-sm leading-[1.3125] w-[3.5rem]">
                  <button onClick={(e:any) => {handleSelectRow(job);e.stopPropagation()}} className="flex items-center">
                    {!selectedRows.map(item => item.id).includes(job.id) ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <g clipPath="url(#clip0_8574_230)">
                          <path
                            d="M19 5V19H5V5H19ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z"
                            fill="#808080"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_8574_230">
                            <rect width="24" height="24" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <g clipPath="url(#clip0_8574_236)">
                          <path
                            d="M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.11 21 21 20.1 21 19V5C21 3.9 20.11 3 19 3ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"
                            fill="#FF861A"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_8574_236">
                            <rect width="24" height="24" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    )}
                  </button>
                </td>
                )}
                <td className="px-2 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 w-[5.625rem]">{job.id}</td>
                <td
                  onClick={() => handleClickOnTitle(job)}
                  className={`${staffPermission[PERMISSIONS.JOB_DELETE] ? "px-2" : "px-4"} ${job.status?.value != RECRUITMENT_STATUS.PRIVATE && "hover:cursor-pointer"} py-4 text-left font-normal text-sm leading-[1.3125] !min-w-[12rem]`}
                >
                  <p className="alu-text-base-2">{job.title}</p>
                  {job?.periodic_task_status && job?.periodic_task_status?.status == PERIODIC_TASK_STATUS.EXPIRED && (
                    <div className="mt-1 flex gap-1">
                      <span className="mt-[2px]"><InfoIcon /> </span>
                      <span className="alu-text-base-2 text-xs font-normal alu-text-error whitespace-pre-wrap">{getPeriodicTaskStatus(job?.periodic_task_status)}</span>
                    </div>
                  )}
                </td>
                <td onMouseEnter={(e:any) => e.currentTarget.parentElement.classList.remove("hover:bg-[#fff3e5]")}
                    onMouseLeave={(e:any) => e.currentTarget.parentElement.classList.add("hover:bg-[#fff3e5]")} 
                    className="px-2 py-4 text-left font-normal text-sm leading-[1.3125] w-[9rem]">
                  {/* {job.status} */}
                  <Dropdown
                    customStyle={`pr-[0.125rem] !bg-[#F7F7F7] ${getAccessStatusOption(job) || staffInfo?.type == STAFF_TYPE.GENERAL ? "" : "hover:!bg-[#FFF3E5]"} `}
                    inputWidth="full"
                    options={jobStatusOptions}
                    value={getSelectedStatusOption(job)}
                    onChange={(value: any) => handleUpdateJobStatus(value, job.id)}
                    disabled={getAccessStatusOption(job)}
                  />
                </td>
                <td className="px-2 py-4 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 w-[8rem]">
                  {job.type}
                </td>
                <td className="break-words whitespace-normal px-2 py-4 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 w-[8rem]">
                  {job.employmentStatus}
                </td>
                <td className="px-2 py-4 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 w-[8rem]">
                  {formatDateTime(job.date)}
                </td>
                <td className="px-2 py-4 text-right font-normal text-sm leading-[1.3125] alu-text-base-2 max-w-[6rem] min-w-[4rem]">
                  {job.views}
                </td>
                <td className="px-2 py-4 text-right font-normal text-sm leading-[1.3125] alu-text-base-2 max-w-[6rem] min-w-[4rem]">
                  {job.saveCount}
                </td>
                <td className="px-2 py-4 text-right font-normal text-sm leading-[1.3125] alu-text-base-2 max-w-[6rem] min-w-[4rem]">
                  {job.applications}
                </td>
                <td
                  onClick={() => handleClickOnDetailBtn(job)}
                  className={`w-[5rem] px-4 py-4 text-center font-normal text-sm leading-[1.3125] ${job.is_public || job.status?.value != RECRUITMENT_STATUS.PRIVATE ? "hover:cursor-pointer alu-text-title" : "text-[#BBB]"}`}
                >
                  詳細
                </td>
                {staffPermission[PERMISSIONS.JOB_EDIT] && (
                <td
                  onClick={() => navigate("/company/job/edit/" + job.id)}
                  className="w-[5rem] hover:cursor-pointer px-4 py-4 text-center font-normal text-sm leading-[1.3125] alu-text-title"
                >
                  編集
                </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {countItems > pageSize && (
      <div className="flex items-center justify-center mt-6">
        <CustomPagination 
          total={countItems} 
          currentPage={currentPage}
          pageSize={pageSize} 
          onPageChange={handleChangePage} 
        />
      </div>
      )}
      {open && confirmText && (
        <ConfirmPopup
          type="delete"
          open={open}
          onClose={() => setOpen(false)}
          confirmContent={confirmText}
          onConfirm={(e: any) => handleConfirm(e)}
        />
      )}
    </>
  );
};

export default JobList;
